import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Sürüşmə mövqeyinin idarəsi.
 *
 * İki ayrı davranış lazımdır:
 *   • YENİ səhifə (klik) → yuxarıdan başlayır. Tək səhifəli tətbiqdə brauzer
 *     mövqeyi olduğu kimi saxlayır, ona görə siyahıda aşağı enib məhsula
 *     toxunan müştəri məhsulu ORTASINDAN görürdü.
 *   • GERİ (düymə və ya iOS-da swipe) → müştəri qaldığı yerə qayıdır.
 *     Kateqoriya seçib onlarla məhsul aşağı enən adam geri qayıdanda yenidən
 *     yuxarıdan başlamamalıdır.
 *
 * **Niyə brauzerin öz bərpasına güvənmirik.** Geri qayıdanda siyahı yenidən
 * qurulur; həmin an səhifə qısa ola bilər və brauzerin bərpa etmək istədiyi
 * mövqe sadəcə MÖVCUD DEYİL — sürüşmə sıfıra qırxılır, məzmun sonra gəlir,
 * mövqe itir. iOS Safari-də swipe-da bu xüsusilə etibarsızdır. Ona görə
 * `scrollRestoration = "manual"` qoyub mövqeni tarixçə açarına görə özümüz
 * saxlayırıq.
 *
 * Mağaza siyahısı react-query ilə keşlənir (`pages/Shop.jsx`), yəni geri
 * qayıdanda məhsullar İLK KADRDA çəkilir və bərpa sıçrayışsız alınır.
 */

// Tarixçə açarı → sürüşmə mövqeyi. Səhifə tam yenilənəndə onsuz da yeni
// tarixçə yaranır, ona görə yaddaşda saxlamaq kifayətdir.
const positions = new Map();

// Məzmun gec gələrsə bu qədər gözləyirik, sonra əlimizdəkinə görə bərpa edirik.
const MAX_WAIT_MS = 1500;
const RETRY_MS = 50;

export const ScrollManager = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const keyRef = useRef(location.key);

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  /* Mövqe ÜÇ yerdə yazılır. Üçü də lazımdır, çünki hər birinin öz kor
   * nöqtəsi var:
   *   • `scroll` — adi hal, mövqe həmişə təzə qalır. Lakin səhifə görünmürsə
   *     (arxa plan tab, gizli pəncərə) brauzer bu hadisəni ÜMUMİYYƏTLƏ
   *     göndərmir — yalnız buna güvənmək olmaz.
   *   • `click` (tutma mərhələsi) — keçiddən DƏRHAL ƏVVƏL, DOM hələ
   *     dəyişməmiş, sürüşmə hələ qırxılmamış. Linkə toxunuş bu yolla tutulur.
   *   • `popstate` — geri/irəli basılanda tərk edilən səhifənin mövqeyi.
   */
  useEffect(() => {
    const save = () => positions.set(keyRef.current, window.scrollY);
    window.addEventListener("scroll", save, { passive: true });
    window.addEventListener("click", save, { capture: true, passive: true });
    window.addEventListener("popstate", save);
    return () => {
      window.removeEventListener("scroll", save);
      window.removeEventListener("click", save, { capture: true });
      window.removeEventListener("popstate", save);
    };
  }, []);

  /* ⚠️ AÇAR `useLayoutEffect`-DƏ DƏYİŞİR, adi `useEffect`-də YOX.
   *
   * Səbəb incədir və bir dəfə bizi aldatdı. Uzun siyahıdan (məsələn 8200 px)
   * qısa məhsul səhifəsinə keçəndə brauzer sürüşməni yeni səhifənin
   * hündürlüyünə QIRXIR və bu, «scroll» hadisəsi yaradır. Adi `useEffect`
   * kadrdan SONRA işlədiyi üçün həmin hadisə hələ KÖHNƏ açar qeydiyyatda
   * ikən gəlib çatır — nəticədə siyahının yadda saxlanmış mövqeyi 8200
   * yerinə qırxılmış dəyərlə (məhsul səhifəsinin maksimumu ilə) əzilir və
   * geri qayıdanda müştəri səhv yerə düşür.
   *
   * `useLayoutEffect` DOM dəyişikliyindən dərhal sonra, kadrdan ƏVVƏL
   * işləyir: açar vaxtında dəyişir və qırxılma yeni açara yazılır, köhnəsi
   * toxunulmamış qalır.
   */
  useLayoutEffect(() => {
    keyRef.current = location.key;
  }, [location.key]);

  // Bərpa da `useLayoutEffect`-dədir: mövqe kadrdan əvvəl qoyulur, yəni
  // istifadəçi əvvəlcə yuxarını, sonra sıçrayışı görmür.
  useLayoutEffect(() => {
    if (navigationType !== "POP") {
      window.scrollTo(0, 0);
      return undefined;
    }

    const target = positions.get(location.key) || 0;
    if (target <= 0) {
      window.scrollTo(0, 0);
      return undefined;
    }

    const roomEnough = () =>
      document.documentElement.scrollHeight - window.innerHeight >= target;

    // Adi hal: siyahı keşdən gəlib, səhifə artıq lazımi hündürlükdədir.
    if (roomEnough()) {
      window.scrollTo(0, target);
      return undefined;
    }

    // Məzmun hələ gəlməyibsə qısa aralıqlarla yoxlayırıq.
    // ⚠️ `requestAnimationFrame` İŞLƏTMİRİK: səhifə görünmür və ya arxa planda
    // olanda kadrlar dayandırılır və bərpa SƏSSİZCƏ baş tutmur.
    let timer = 0;
    let cancelled = false;
    const startedAt = Date.now();

    const tick = () => {
      if (cancelled) return;
      if (roomEnough() || Date.now() - startedAt > MAX_WAIT_MS) {
        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
        window.scrollTo(0, Math.max(0, Math.min(target, maxScroll)));
        return;
      }
      timer = setTimeout(tick, RETRY_MS);
    };
    timer = setTimeout(tick, RETRY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [location.key, navigationType]);

  return null;
};
